import { formatDate } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatCommandTypeEnum,
  ChatMessage,
  ChatMessageTypeEnum,
  ChatService,
  ChatSocketService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import {
  chatFileIcon,
  chatMessageTypeFromContentType,
  formatChatFileSize,
} from '../chat-composer/chat-attachment.util';
import { ChatComposerSend, ChatMentionMember } from '../chat-composer/chat-composer.component';
import { ChatMentionRender } from '../pipes/chat-markdown.renderer';
import * as ChatActions from '../state/chat.actions';
import { userDisplaySelector } from '../state/chat.selectors';
import { resolveChatInitials } from '../chat-initials.util';
import { collectChatImages, openChatImageViewer } from '../dialogs/chat-image-viewer-dialog/chat-image-viewer.opener';
import { ChatDialogService } from '../services/chat-dialog.service';

/** Local send-state decoration over the wire shape (same contract as the pane). */
type ChatThreadMessageView = ChatMessage & {
  sendState?: 'sending' | 'sent' | 'failed';
  deleted?: boolean;
  sendGroup?: string;
};

/** Precomputed render row (root and replies share the shape). */
interface ChatThreadRow {
  key: string;
  message: ChatThreadMessageView;
  authorName: string;
  authorInitial: string;
  authorImage?: string;
  timeLabel: string;
  own: boolean;
  pending: boolean;
  reactions: Array<{ emoji: string, count: number, mine: boolean }>;
  fileIcon?: string;
  fileSizeLabel?: string;
  /** `@Name` tokens the chatMarkdown pipe turns into chips. */
  mentionRender?: ChatMentionRender[];
  /** Forwarded banner (RF-13) — a thread can be rooted on a forwarded copy. */
  forwardedLabel?: string;
}

const THREAD_PAGE_LIMIT = 50;
const PENDING_ID_PREFIX = 'pending:';

/**
 * Internal Chat thread panel (SPEC-2026-07-internal-chat, T-20 · RF-12/AC-10).
 *
 * Hosted INSIDE the conversation pane so both surfaces get it for free: a
 * right-side drawer in the `/chat` full view and a full-cover swap in the bubble
 * popup (`compact`). Root + replies come from
 * `GET /api/chat/messages/:id/thread` (parent + cursor-paginated replies, newest
 * page first — `before` pages further back with the OPAQUE `meta.nextCursor`).
 *
 * The same HARD RULE as the main pane applies: replies live in a
 * COMPONENT-LOCAL array, hydrated once and appended from the socket with dedup
 * by `_id` and by `clientMessageId` — never re-dispatched to the store.
 *
 * Replies are sent with `THREAD_REPLY { channelId, parentMessage, … }`.
 * CONTRACT NOTE: `MessageService.threadReply` delegates to the send pipeline
 * with `parentMessage` set and offers NO "also send to the channel" option
 * (there is no such flag in the payload, and the API keeps `parentMessage != null`
 * messages out of the main flow) — so that toggle is deliberately absent here.
 */
@Component({
  selector: 'chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatThreadComponent extends AbstractComponent implements OnInit, OnChanges {

  @ViewChild('threadScroll') threadScroll: ElementRef<HTMLDivElement>;

  /** Thread root message id — changing it reloads the panel. */
  @Input() rootMessageId: string | null = null;

  /** Conversation hosting the thread (THREAD_REPLY target). */
  @Input() channelId: string | null = null;

  /** Popup mode — the panel covers the pane instead of docking beside it. */
  @Input() compact = false;

  /** Mentionable members forwarded to the reply composer. */
  @Input() members: ChatMentionMember[] = [];

  /** Sends are refused server-side on archived conversations. */
  @Input() disabled = false;

  @Output() closed = new EventEmitter<void>();

  readonly messageType = ChatMessageTypeEnum;

  rootRow: ChatThreadRow | null = null;
  rows: ChatThreadRow[] = [];

  loadingThread = false;
  loadingOlder = false;
  loadError = false;
  hasMore = false;

  private messages: ChatThreadMessageView[] = [];
  private root: ChatThreadMessageView | null = null;
  private userDisplay: ChatUserDisplayMap = {};
  private cursor: string | null = null;

  /** THREAD_REPLY payload per clientMessageId — kept for idempotent retries. */
  private readonly sendPayloads = new Map<string, Record<string, unknown>>();

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatService: ChatService,
    private readonly chatSocketService: ChatSocketService,
    private readonly dialog: ChatDialogService,
  ) {
    super(injector);
  }

  /** The viewer over every image of the thread, starting on this one (DEV-2062). */
  openImage(row: ChatThreadRow): void {
    const message = row.message;
    if (!message?.fileUrl) return;
    openChatImageViewer(this.dialog, collectChatImages(this.rows.map((entry) => entry.message)), message._id || message.clientMessageId || '');
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.store.select(userDisplaySelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userDisplay) => {
        this.userDisplay = userDisplay;
        this.buildRows();
      });

    // Thread replies fan out on the CHANNEL room like any other message — only
    // the ones whose parentMessage is this root belong here.
    this.chatSocketService.messageNew$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => this.onMessageNew(message));

    this.chatSocketService.messageUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        if (!message) return;
        if (message._id === this.rootMessageId) {
          this.root = { ...this.root, ...message } as ChatThreadMessageView;
          this.buildRows();
          return;
        }
        this.patchMessage(message._id, (local) => ({ ...local, ...message }));
      });

    this.chatSocketService.messageDeleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (!data?.messageId) return;
        if (data.messageId === this.rootMessageId && this.root) {
          this.root = { ...this.root, deleted: true };
          this.buildRows();
          return;
        }
        this.patchMessage(data.messageId, (local) => ({ ...local, deleted: true }));
      });

    this.chatSocketService.reactionUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (!data?.messageId) return;
        if (data.messageId === this.rootMessageId && this.root) {
          this.root = { ...this.root, reactions: data.reactions ?? [] };
          this.buildRows();
          return;
        }
        this.patchMessage(data.messageId, (local) => ({ ...local, reactions: data.reactions ?? [] }));
      });

    this.chatSocketService.onReconnect$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.load());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rootMessageId']) {
      this.resetThread();
      if (this.rootMessageId) this.load();
    }
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  load(): void {
    if (!this.rootMessageId) return;
    const rootMessageId = this.rootMessageId;
    this.loadingThread = true;
    this.loadError = false;

    this.chatService.getThread(rootMessageId, { limit: THREAD_PAGE_LIMIT })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (rootMessageId !== this.rootMessageId) return; // switched meanwhile
          this.root = (envelope?.data?.parent ?? null) as ChatThreadMessageView | null;
          this.messages = (envelope?.data?.replies ?? []) as ChatThreadMessageView[];
          this.hasMore = envelope?.meta?.hasMore ?? false;
          this.cursor = envelope?.meta?.nextCursor ?? null;
          this.mergeUserDisplay(envelope?.meta?.userDisplay);
          this.loadingThread = false;
          this.buildRows();
          this.markThreadSeen();
          this.scrollToBottom();
        },
        error: () => {
          if (rootMessageId !== this.rootMessageId) return;
          this.loadingThread = false;
          this.loadError = true;
        },
      });
  }

  loadOlder(): void {
    if (!this.rootMessageId || !this.cursor || this.loadingOlder) return;
    const rootMessageId = this.rootMessageId;
    this.loadingOlder = true;

    this.chatService.getThread(rootMessageId, { before: this.cursor, limit: THREAD_PAGE_LIMIT })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (rootMessageId !== this.rootMessageId) return;
          const older = (envelope?.data?.replies ?? []) as ChatThreadMessageView[];
          const known = new Set(this.messages.map((message) => message._id));
          this.messages = [...older.filter((message) => !known.has(message._id)), ...this.messages];
          this.hasMore = envelope?.meta?.hasMore ?? false;
          this.cursor = envelope?.meta?.nextCursor ?? null;
          this.mergeUserDisplay(envelope?.meta?.userDisplay);
          this.loadingOlder = false;
          this.buildRows();
        },
        error: () => this.loadingOlder = false,
      });
  }

  retryLoad(): void {
    this.load();
  }

  close(): void {
    this.closed.emit();
  }

  // ---------------------------------------------------------------------------
  // Sending (THREAD_REPLY — optimistic append reconciled by ack + fan-out)
  // ---------------------------------------------------------------------------

  onComposerSend(event: ChatComposerSend): void {
    const content = event?.content ?? '';
    const attachments = event?.attachments ?? [];
    if (!content && attachments.length === 0) return;
    if (!this.channelId || !this.rootMessageId) return;

    const clientMessageId = this.generateClientMessageId();
    const mentions = event.mentions ?? [];
    const now = new Date();
    // Work reference (RF-35) — `threadReply` delegates to `processSend`, so the
    // same carrier rule applies: text message when content exists, otherwise
    // the first attachment.
    const contextRef = event.contextRef ?? undefined;

    // Mirrors the backend split (processSend, reached through threadReply): one
    // media message per attachment (clientMessageId suffixed -0..-9) plus one
    // TEXT message when content exists — all under ONE THREAD_REPLY command.
    const group: ChatThreadMessageView[] = attachments.map((attachment, index) => ({
      _id: `${PENDING_ID_PREFIX}${clientMessageId}-${index}`,
      channel: this.channelId,
      sender: this.getIDCurrentUser(),
      company: this.getIDCurrentCompany(),
      type: chatMessageTypeFromContentType(attachment.contentType),
      content: '',
      fileUrl: attachment.fileUrl,
      contentType: attachment.contentType,
      fileName: attachment.fileName,
      fileSize: attachment.fileSize,
      clientMessageId: `${clientMessageId}-${index}`,
      parentMessage: this.rootMessageId,
      threadCount: 0,
      reactions: [],
      mentions: (!content && index === 0) ? mentions : [],
      contextRef: (!content && index === 0) ? contextRef : undefined,
      removed: false,
      createdAt: now,
      updatedAt: now,
      sendState: 'sending' as const,
      sendGroup: clientMessageId,
    }));

    if (content) {
      group.push({
        _id: `${PENDING_ID_PREFIX}${clientMessageId}`,
        channel: this.channelId,
        sender: this.getIDCurrentUser(),
        company: this.getIDCurrentCompany(),
        type: ChatMessageTypeEnum.TEXT,
        content,
        clientMessageId,
        parentMessage: this.rootMessageId,
        threadCount: 0,
        reactions: [],
        mentions,
        contextRef,
        removed: false,
        createdAt: now,
        updatedAt: now,
        sendState: 'sending',
        sendGroup: clientMessageId,
      });
    }

    this.messages = [...this.messages, ...group];
    this.buildRows();
    this.scrollToBottom();

    this.sendPayloads.set(clientMessageId, {
      channelId: this.channelId,
      parentMessage: this.rootMessageId,
      clientMessageId,
      ...(content ? { content } : {}),
      ...(mentions.length ? { mentions } : {}),
      ...(attachments.length ? { attachments } : {}),
      ...(contextRef ? { contextRef } : {}),
    });

    this.dispatchSend(clientMessageId);
  }

  retrySend(row: ChatThreadRow): void {
    const message = row.message;
    if (!message?.sendGroup || message.sendState !== 'failed') return;
    if (!this.sendPayloads.has(message.sendGroup)) return;
    this.patchBySendGroup(message.sendGroup, (local) =>
      (local.sendState === 'failed' ? { ...local, sendState: 'sending' } : local));
    this.dispatchSend(message.sendGroup);
  }

  discardSend(row: ChatThreadRow): void {
    const message = row.message;
    if (!message || message.sendState !== 'failed') return;
    if (message.sendGroup) {
      this.messages = this.messages.filter((local) =>
        !(local.sendGroup === message.sendGroup && local.sendState === 'failed'));
      this.sendPayloads.delete(message.sendGroup);
    } else {
      this.messages = this.messages.filter((local) => local._id !== message._id);
    }
    this.buildRows();
  }

  onTypingChange(typing: boolean): void {
    if (!this.channelId) return;
    this.chatSocketService.typing(this.channelId, typing, this.getNameCurrentUser());
  }

  toggleReaction(row: ChatThreadRow, emoji: string): void {
    const message = row.message;
    if (!message || message.deleted || row.pending) return;

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.REACTION_TOGGLE, { messageId: message._id, emoji })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { /* authoritative reactions arrive on chat:reaction:updated */ },
        error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
      });
  }

  trackRow(_index: number, row: ChatThreadRow): string {
    return row.key;
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private dispatchSend(groupId: string): void {
    const payload = this.sendPayloads.get(groupId);
    if (!payload) return;

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.THREAD_REPLY, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendPayloads.delete(groupId);
          this.patchBySendGroup(groupId, (local) => ({ ...local, sendState: 'sent' }));
        },
        error: (ack) => {
          this.patchBySendGroup(groupId, (local) =>
            // A late error ack must never contradict a delivered fan-out.
            (local.sendState === 'sending' || local._id.startsWith(PENDING_ID_PREFIX)
              ? { ...local, sendState: 'failed' }
              : local));
          this.alertService.error(this.resolveAckError(ack?.errorKey, 'chat.errors.sendFailed'));
        },
      });
  }

  private onMessageNew(message: ChatMessage): void {
    if (!message || !this.rootMessageId) return;
    if (String(message.parentMessage ?? '') !== this.rootMessageId) return;
    if (this.messages.some((local) => local._id === message._id)) return;

    const optimisticIndex = message.clientMessageId
      ? this.messages.findIndex((local) => local.clientMessageId === message.clientMessageId)
      : -1;

    if (optimisticIndex >= 0) {
      const updated = [...this.messages];
      updated[optimisticIndex] = { ...message, sendState: 'sent' };
      this.messages = updated;
    } else {
      this.messages = [...this.messages, { ...message }];
    }
    this.buildRows();
    this.markThreadSeen();
    this.scrollToBottom();
  }

  private patchMessage(messageId: string, patch: (message: ChatThreadMessageView) => ChatThreadMessageView): void {
    const index = this.messages.findIndex((message) => message._id === messageId);
    if (index === -1) return;
    const updated = [...this.messages];
    updated[index] = patch(updated[index]);
    this.messages = updated;
    this.buildRows();
  }

  private patchBySendGroup(groupId: string, patch: (message: ChatThreadMessageView) => ChatThreadMessageView): void {
    let touched = false;
    const updated = this.messages.map((message) => {
      if (message.sendGroup !== groupId) return message;
      touched = true;
      return patch(message);
    });
    if (!touched) return;
    this.messages = updated;
    this.buildRows();
  }

  private resetThread(): void {
    this.root = null;
    this.rootRow = null;
    this.messages = [];
    this.rows = [];
    this.loadingThread = false;
    this.loadingOlder = false;
    this.loadError = false;
    this.hasMore = false;
    this.cursor = null;
    this.sendPayloads.clear();
  }

  /** Records the reply count I have seen so the main-flow chip drops its dot. */
  private markThreadSeen(): void {
    if (!this.rootMessageId) return;
    // Locally-known replies are the honest "seen" figure: the paginated page may
    // be a subset, and threadCount on the root can already be ahead of it.
    const seen = Math.max(this.messages.length, this.root?.threadCount ?? 0);
    this.store.dispatch(ChatActions.setThreadSeen({ messageId: this.rootMessageId, count: seen }));
  }

  private mergeUserDisplay(userDisplay?: ChatUserDisplayMap): void {
    if (!userDisplay) return;
    this.userDisplay = { ...this.userDisplay, ...userDisplay };
    // Thread authors may be unknown to the sidebar snapshot — share them.
    this.store.dispatch(ChatActions.mergeUserDisplay({ userDisplay }));
  }

  private buildRows(): void {
    this.rootRow = this.root ? this.buildRow(this.root) : null;
    this.rows = this.messages.map((message) => this.buildRow(message));
  }

  private buildRow(message: ChatThreadMessageView): ChatThreadRow {
    const created = new Date(message.createdAt);
    const display = this.userDisplay[message.sender];
    const authorName = display?.name ?? this.t('chat.thread.unknownAuthor');
    const isMedia = message.type !== ChatMessageTypeEnum.TEXT;

    return {
      key: message._id,
      message,
      authorName,
      authorInitial: resolveChatInitials(authorName || '?'),
      authorImage: display?.image,
      timeLabel: formatDate(created, 'dd/MM HH:mm', 'en-US'),
      own: message.sender === this.getIDCurrentUser(),
      pending: message.sendState === 'sending'
        || message.sendState === 'failed'
        || message._id.startsWith(PENDING_ID_PREFIX),
      reactions: (message.reactions ?? [])
        .filter((reaction) => (reaction.users ?? []).length > 0)
        .map((reaction) => ({
          emoji: reaction.emoji,
          count: reaction.users.length,
          mine: reaction.users.includes(this.getIDCurrentUser()),
        })),
      fileIcon: isMedia ? chatFileIcon(message.contentType, message.fileName) : undefined,
      fileSizeLabel: (isMedia && message.fileSize) ? formatChatFileSize(message.fileSize) : undefined,
      mentionRender: (message.mentions ?? [])
        .map((id) => ({
          name: this.userDisplay[id]?.name ?? '',
          self: id === this.getIDCurrentUser(),
        }))
        .filter((mention) => !!mention.name),
      forwardedLabel: this.resolveForwardedLabel(message),
    };
  }

  /** Same banner rule as the pane: named when the snapshot resolves. */
  private resolveForwardedLabel(message: ChatThreadMessageView): string | undefined {
    if (!message.forwardedFrom && !message.forwardedFromSender) return undefined;
    const name = message.forwardedFromSender
      ? this.userDisplay[message.forwardedFromSender]?.name
      : undefined;
    return name
      ? this.t('chat.conversation.forwardedFrom', { name })
      : this.t('chat.conversation.forwarded');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.threadScroll?.nativeElement;
      if (element) element.scrollTop = element.scrollHeight;
    });
  }

  private resolveAckError(errorKey: string | undefined, fallbackKey = 'chat.errors.commandFailed'): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t(fallbackKey);
  }

  private generateClientMessageId(): string {
    const cryptoObj: Crypto | undefined = globalThis.crypto;
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
    return `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
